import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSettings } from '../contexts/SettingsContext';
import { useTheme } from '../contexts/ThemeContext';
import aiAutomationApi from '../utils/aiAutomationApi';

/* Figma: SUN 21, MON 22 - uppercase 3-letter day */
const DAYS_UPPER = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const HOURS = Array.from({ length: 11 }, (_, i) => i + 7); // 7 AM - 5 PM

/**
 * InterviewDetailsModal - Shows interviews list for a specific day/time cell
 */
const InterviewDetailsModal = ({ isOpen, onClose, interviews, selectedDate, selectedHour, primaryColor, showAllUpcoming = false }) => {
  const { isDark } = useTheme();
  const [selectedInterviewForNotes, setSelectedInterviewForNotes] = useState(null);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [notesData, setNotesData] = useState(null);
  const [notesLoading, setNotesLoading] = useState(false);
  const [joinMeetLoading, setJoinMeetLoading] = useState({});
  const [candidateDetails, setCandidateDetails] = useState(null);
  const [candidateSkills, setCandidateSkills] = useState([]);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [regenerateLoading, setRegenerateLoading] = useState(false);
  const [showConversation, setShowConversation] = useState(false);

  const handleJoinMeet = async (interview) => {
    const interviewId = interview.id;
    setJoinMeetLoading(prev => ({ ...prev, [interviewId]: true }));
    try {
      console.log('[InterviewCalendar] Join Meet clicked', { interview_id: interviewId, google_meet_link: interview.google_meet_link, interview_title: interview.interview_title });
      const response = await aiAutomationApi.post(`/interviews/${interviewId}/join-meeting`, {});
      console.log('[InterviewCalendar] Join Meet response:', response);
      if (response.success) {
        // Open the Google Meet link in a new tab
        if (interview.google_meet_link) {
          window.open(interview.google_meet_link, '_blank');
        }
        alert(`AI Note Taker bot has been requested to join the meeting!\nMeeting ID: ${response.meeting_id}\nBot ID: ${response.bot_id}`);
      } else {
        alert(`Failed to join meeting: ${response.message || response.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('[InterviewCalendar] Failed to join meeting:', err);
      alert(`Failed to join meeting: ${err.message || 'Unknown error'}`);
    } finally {
      setJoinMeetLoading(prev => {
        const next = { ...prev };
        delete next[interviewId];
        return next;
      });
    }
  };

  const fetchCandidateDetailsAndSkills = async (interviewId) => {
    try {
      console.log('[InterviewCalendar] Fetching candidate details and skills for:', interviewId);
      const data = await aiAutomationApi.get(`/interviews/${interviewId}/candidate-info`);
      console.log('[InterviewCalendar] Candidate info response:', data);
      if (data.success) {
        setCandidateDetails(data);
        setCandidateSkills(data.skills || []);
      }
    } catch (err) {
      console.error('[InterviewCalendar] Failed to fetch candidate info:', err);
    }
  };

  const handleAnalyzeWithAI = async (interviewId, force = false) => {
    if (!interviewId) return;
    setAnalysisLoading(true);
    try {
      console.log('[InterviewCalendar] Analyzing interview with AI:', interviewId);
      const data = await aiAutomationApi.post(`/interviews/${interviewId}/notes/analyze`, force ? { force: true } : {});
      console.log('[InterviewCalendar] AI Analysis response:', data);
      setAiAnalysis(data);
      
      // If notes are not ready, keep loading state
      if (data.status === 'notes_not_ready') {
        // Keep analysisLoading true to show loader
        return;
      }
    } catch (err) {
      console.error('[InterviewCalendar] Failed to analyze interview:', err);
      setAiAnalysis({ success: false, error: err.message || 'Failed to generate AI analysis' });
    } finally {
      setAnalysisLoading(false);
    }
  };

  const handleRegenerateNotes = async (interviewId) => {
    if (!interviewId) return;
    setRegenerateLoading(true);
    setNotesLoading(true);
    setAnalysisLoading(false);
    setAiAnalysis(null);
    try {
      console.log('[InterviewCalendar] Regenerating AI notes for interview:', interviewId);
      const data = await aiAutomationApi.post(`/interviews/${interviewId}/notes/regenerate`, {});
      console.log('[InterviewCalendar] Regenerated notes response:', data);
      setNotesData(data);
      await fetchCandidateDetailsAndSkills(interviewId);
      await handleAnalyzeWithAI(interviewId, true);
    } catch (err) {
      console.error('[InterviewCalendar] Failed to regenerate notes:', err);
      setNotesData({ success: false, error: err.message || 'Could not regenerate notes from AI Note Taker' });
    } finally {
      setRegenerateLoading(false);
      setNotesLoading(false);
    }
  };

  const handleViewNotes = async (interview) => {
    setSelectedInterviewForNotes(interview);
    setNotesModalOpen(true);
    setNotesLoading(true);
    setAnalysisLoading(false);
    setAiAnalysis(null);
    setCandidateDetails(null);
    setCandidateSkills([]);
    setShowConversation(false);
    
    try {
      console.log('[InterviewCalendar] View Notes clicked', { interview_id: interview.id });
      const data = await aiAutomationApi.get(`/interviews/${interview.id}/notes`);
      console.log('[InterviewCalendar] Notes response:', data);
      setNotesData(data);
      setAiAnalysis(data?.meeting_analysis_json?.success ? data.meeting_analysis_json : null);
      
      // Fetch candidate details and skills
      await fetchCandidateDetailsAndSkills(interview.id);
      
      // Only call OpenAI if cached analysis is not already available.
      if (!data?.meeting_analysis_json?.success) {
        console.log('[InterviewCalendar] Auto-triggering AI analysis for interview:', interview.id);
        await handleAnalyzeWithAI(interview.id);
      }
    } catch (err) {
      console.error('[InterviewCalendar] Failed to load notes:', err);
      setNotesData({ success: false, error: err.message || 'Could not fetch notes from AI Note Taker' });
    } finally {
      setNotesLoading(false);
    }
  };

  const formatDateDisplay = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Determine header text based on context
  let headerText;
  if (showAllUpcoming) {
    headerText = 'Upcoming Interviews';
  } else if (selectedHour !== null) {
    headerText = `Interviews on ${formatDateDisplay(selectedDate)} at ${selectedHour === 12 ? '12 PM' : selectedHour > 12 ? `${selectedHour - 12} PM` : `${selectedHour} AM`}`;
  } else {
    headerText = `Interviews on ${formatDateDisplay(selectedDate)}`;
  }

  if (!isOpen && !notesModalOpen) return null;

  return (
    <>
      {isOpen && createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal - Increased width to max-w-6xl for better table display */}
      <div className={`relative w-full max-w-6xl max-h-[85vh] overflow-hidden rounded-xl shadow-2xl ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {headerText}
          </h3>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content - Increased max-height for more content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[70vh]">
          {interviews.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No interviews scheduled for this time slot.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[800px]">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">Date</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">Time</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">Candidate</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">Job Title</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">Interviewer</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {interviews.map((interview, idx) => (
                    <tr key={interview.id || idx} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <td className="py-3 px-4 text-gray-900 dark:text-white">
                        {interview.scheduled_date ? new Date(interview.scheduled_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                      </td>
                      <td className="py-3 px-4 text-gray-900 dark:text-white">
                        {formatTime(interview.scheduled_time)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-gray-900 dark:text-white font-medium">
                          {interview.candidate_name || 'N/A'}
                        </div>
                        <div className="text-gray-500 dark:text-gray-400 text-xs">
                          {interview.candidate_email || ''}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-900 dark:text-white">
                        {interview.interview_title || interview.job_title || '-'}
                      </td>
                      <td className="py-3 px-4 text-gray-900 dark:text-white">
                        {interview.interviewer_name || '-'}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {interview.google_meet_link ? (
                            <>
                              <button
                                onClick={() => handleJoinMeet(interview)}
                                disabled={joinMeetLoading[interview.id]}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{ backgroundColor: primaryColor }}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                                {joinMeetLoading[interview.id] ? 'Joining...' : 'Join Meet'}
                              </button>
                              <button
                                onClick={() => handleViewNotes(interview)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-opacity hover:opacity-90"
                                style={{ borderColor: primaryColor, color: primaryColor, backgroundColor: 'transparent' }}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Notes
                              </button>
                            </>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500 text-xs">No link</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
      )}
      {notesModalOpen && createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setNotesModalOpen(false)}>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">AI Meeting Notes</h2>
            {selectedInterviewForNotes && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {selectedInterviewForNotes.candidate_name} ({selectedInterviewForNotes.candidate_email})
              </p>
            )}
          </div>
          <button onClick={() => setNotesModalOpen(false)} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {notesLoading ? (
            <div className="text-center py-8 text-gray-500">Loading notes...</div>
          ) : !notesData ? (
            <div className="text-center py-8 text-gray-500">No notes data</div>
          ) : notesData.success === false ? (
            <div className="text-center py-8">
              <p className="text-red-500 mb-2">{notesData.message || notesData.error || 'Failed to load notes'}</p>
              <p className="text-sm text-gray-500">{notesData.status === 'unknown' ? 'Click "Join Meet" first to start the bot.' : 'Please try again later.'}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {notesData.notes?.executive_summary && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Executive Summary</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{notesData.notes.executive_summary}</p>
                </div>
              )}
              {notesData.notes?.key_decisions && Array.isArray(notesData.notes.key_decisions) && notesData.notes.key_decisions.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Key Decisions</h3>
                  <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    {notesData.notes.key_decisions.map((decision, i) => (
                      <li key={i}>{decision}</li>
                    ))}
                  </ul>
                </div>
              )}
              {notesData.notes?.action_items && Array.isArray(notesData.notes.action_items) && notesData.notes.action_items.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Action Items</h3>
                  <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    {notesData.notes.action_items.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
              {notesData.notes?.transcript && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Transcript</h3>
                  <div className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 p-3 rounded max-h-60 overflow-y-auto">
                    <pre className="whitespace-pre-wrap font-sans">{notesData.notes.transcript}</pre>
                  </div>
                </div>
              )}
              {notesData.recording_url && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Recording</h3>
                  <audio controls className="w-full">
                    <source src={notesData.recording_url} />
                    Your browser does not support the audio tag.
                  </audio>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
      )}
      {notesModalOpen && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setNotesModalOpen(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-7xl mx-4 max-h-[95vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">AI Meeting Notes</h2>
                {selectedInterviewForNotes && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {selectedInterviewForNotes.candidate_name} ({selectedInterviewForNotes.candidate_email})
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => selectedInterviewForNotes && handleRegenerateNotes(selectedInterviewForNotes.id)}
                  disabled={!selectedInterviewForNotes || notesLoading || analysisLoading || regenerateLoading}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: primaryColor }}
                >
                  {regenerateLoading ? 'Regenerating...' : 'Regenerate AI Note'}
                </button>
                <button onClick={() => setNotesModalOpen(false)} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
              {(notesLoading || analysisLoading) ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-gray-300 border-t-blue-600 mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-400 text-lg font-medium">Generating AI analysis...</p>
                  <p className="text-gray-500 dark:text-gray-500 text-sm mt-2">Please wait while we analyze the interview</p>
                  {aiAnalysis?.status === 'notes_not_ready' && (
                    <div className="mt-4">
                      <p className="text-sm text-amber-600 dark:text-amber-400 mb-2">Waiting for meeting notes...</p>
                      <button
                        onClick={() => selectedInterviewForNotes && handleAnalyzeWithAI(selectedInterviewForNotes.id)}
                        className="px-4 py-2 text-sm rounded-lg text-white font-medium"
                        style={{ backgroundColor: primaryColor }}
                      >
                        Retry Analysis
                      </button>
                    </div>
                  )}
                </div>
              ) : !notesData ? (
                <div className="text-center py-8 text-gray-500">No notes data</div>
              ) : notesData.success === false ? (
                <div className="text-center py-8">
                  <p className="text-red-500 mb-2">{notesData.message || notesData.error || 'Failed to load notes'}</p>
                  <p className="text-sm text-gray-500">{notesData.status === 'unknown' ? 'Click "Join Meet" first to start the bot.' : 'Please try again later.'}</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Top Section: Skills Match & Status */}
                  {aiAnalysis?.success && (
                    <div className="bg-gradient-to-r from-blue-50 to-teal-50 dark:from-blue-900/20 dark:to-teal-900/20 rounded-lg p-5">
                      {/* Skills Matching Score Circle and Description */}
                      <div className="flex items-center gap-4 mb-4">
                        <div className="relative w-20 h-20 flex-shrink-0">
                          <svg className="transform -rotate-90 w-20 h-20">
                            <circle cx="40" cy="40" r="32" stroke="currentColor" strokeWidth="6" fill="none" className="text-gray-200 dark:text-gray-700" />
                            <circle
                              cx="40"
                              cy="40"
                              r="32"
                              stroke="currentColor"
                              strokeWidth="6"
                              fill="none"
                              className="text-blue-600 dark:text-blue-400"
                              strokeDasharray={`${2 * Math.PI * 32}`}
                              strokeDashoffset={`${2 * Math.PI * 32 * (1 - (aiAnalysis.skills_matching_score || 0) / 10)}`}
                            />
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-xl font-bold text-blue-700 dark:text-blue-300">{aiAnalysis.skills_matching_score?.toFixed(1) || '0'}</span>
                          </div>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Skills Match</p>
                          {aiAnalysis.summary && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{aiAnalysis.summary}</p>
                          )}
                        </div>
                      </div>
                      {/* Recommendation Badge - On next line */}
                      <div className="flex items-center">
                        <span className={`px-4 py-2 rounded-lg text-sm font-semibold inline-block ${
                          aiAnalysis.recommendation === 'Hire' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                          aiAnalysis.recommendation === 'No Hire' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                          'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                        }`}>
                          {aiAnalysis.recommendation === 'Maybe' ? 'Further Evaluation Needed' : aiAnalysis.recommendation || 'Further Evaluation Needed'}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Candidate Details & Skills with Overall Rating in top-right */}
                  {candidateDetails && (
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-5 relative">
                      {/* Overall Rating in top-right corner */}
                      {aiAnalysis?.success && (
                        <div className="absolute top-4 right-4 text-center">
                          <div className="relative w-16 h-16">
                            <svg className="transform -rotate-90 w-16 h-16">
                              <circle cx="32" cy="32" r="26" stroke="currentColor" strokeWidth="5" fill="none" className="text-gray-200 dark:text-gray-700" />
                              <circle
                                cx="32"
                                cy="32"
                                r="26"
                                stroke="currentColor"
                                strokeWidth="5"
                                fill="none"
                                className="text-teal-600 dark:text-teal-400"
                                strokeDasharray={`${2 * Math.PI * 26}`}
                                strokeDashoffset={`${2 * Math.PI * 26 * (1 - (aiAnalysis.overall_rating || 0) / 10)}`}
                              />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="text-lg font-bold text-teal-700 dark:text-teal-300">{aiAnalysis.overall_rating?.toFixed(1) || '0'}</span>
                            </div>
                          </div>
                          <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mt-1">Overall</p>
                        </div>
                      )}
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Candidate Details</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                        <div><strong className="text-gray-700 dark:text-gray-300">Current Role:</strong> <span className="text-gray-600 dark:text-gray-400">{candidateDetails.current_role || 'N/A'}</span></div>
                        <div><strong className="text-gray-700 dark:text-gray-300">Current Company:</strong> <span className="text-gray-600 dark:text-gray-400">{candidateDetails.current_company || 'N/A'}</span></div>
                        <div><strong className="text-gray-700 dark:text-gray-300">Experience:</strong> <span className="text-gray-600 dark:text-gray-400">{candidateDetails.experience_years || 'N/A'} years</span></div>
                        <div><strong className="text-gray-700 dark:text-gray-300">Location:</strong> <span className="text-gray-600 dark:text-gray-400">{candidateDetails.location || 'N/A'}</span></div>
                      </div>
                      <div>
                        <strong className="text-gray-700 dark:text-gray-300 text-sm">Skills:</strong>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {candidateSkills && candidateSkills.length > 0 ? candidateSkills.map((skill, i) => (
                            <span key={i} className="px-3 py-1 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                              {skill}
                            </span>
                          )) : (
                            <span className="text-sm text-gray-500">No skills available</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* AI Analysis Content */}
                  {aiAnalysis?.success && (
                    <div className="space-y-6">
                      {/* Individual Skill Ratings (including Communication Skills) */}
                      {aiAnalysis.skill_ratings && Array.isArray(aiAnalysis.skill_ratings) && aiAnalysis.skill_ratings.length > 0 && (
                        <div className="bg-white dark:bg-gray-700/50 rounded-lg p-5 border border-gray-200 dark:border-gray-600">
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Skills & Values ({aiAnalysis.skill_ratings.length})</h4>
                          <div className="space-y-4">
                            {aiAnalysis.skill_ratings.map((skillRating, idx) => {
                              const score = parseFloat(skillRating.score) || 0;
                              const percentage = (score / 10) * 100;
                              return (
                                <div key={idx} className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{skillRating.skill}</span>
                                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{score.toFixed(1)} / 10</span>
                                  </div>
                                  <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5">
                                    <div
                                      className="bg-teal-500 h-2.5 rounded-full transition-all"
                                      style={{ width: `${percentage}%` }}
                                    ></div>
                                  </div>
                                  {skillRating.description && (
                                    <p className="text-xs text-gray-600 dark:text-gray-400">{skillRating.description}</p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Key Takeaways */}
                      {aiAnalysis.key_takeaways && Array.isArray(aiAnalysis.key_takeaways) && aiAnalysis.key_takeaways.length > 0 && (
                        <div className="bg-white dark:bg-gray-700/50 rounded-lg p-5 border border-gray-200 dark:border-gray-600">
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Key Takeaways</h4>
                          <ul className="space-y-2">
                            {aiAnalysis.key_takeaways.map((takeaway, idx) => (
                              <li key={idx} className="text-sm text-gray-600 dark:text-gray-400 flex items-start">
                                <span className="text-blue-500 mr-2">•</span>
                                <span>{takeaway}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Summary */}
                  {notesData.notes?.executive_summary && (
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Summary</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                        {notesData.notes.executive_summary}
                      </p>
                    </div>
                  )}

                  {/* Transcript */}
                  {notesData.notes?.transcript && (
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Transcript</h4>
                      <div className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg max-h-60 overflow-y-auto">
                        <pre className="whitespace-pre-wrap font-sans">{notesData.notes.transcript}</pre>
                      </div>
                    </div>
                  )}

                  {/* Meeting Conversation Button */}
                  {notesData.notes?.speakers && Array.isArray(notesData.notes.speakers) && notesData.notes.speakers.length > 0 && (
                    <div>
                      <button
                        onClick={() => setShowConversation(!showConversation)}
                        className="w-full px-4 py-3 text-left bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-colors flex items-center justify-between"
                      >
                        <span className="font-semibold text-blue-700 dark:text-blue-300">Meeting Conversation</span>
                        <svg className={`w-5 h-5 text-blue-700 dark:text-blue-300 transition-transform ${showConversation ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {showConversation && (
                        <div className="mt-4 space-y-3">
                          {(() => {
                            // Build chronological conversation
                            const allMessages = [];
                            notesData.notes.speakers.forEach(speaker => {
                              if (speaker.contributions && Array.isArray(speaker.contributions)) {
                                speaker.contributions.forEach(contrib => {
                                  if (typeof contrib === 'string') {
                                    // Extract timestamp and text: [00:00] text or [00:00:00] text
                                    const match = contrib.match(/^\[(\d{1,2}):(\d{2})(?::(\d{2}))?\]\s*(.*)/);
                                    if (match) {
                                      const hours = match[3] ? parseInt(match[1]) : 0;
                                      const minutes = match[3] ? parseInt(match[2]) : parseInt(match[1]);
                                      const seconds = match[3] ? parseInt(match[3]) : parseInt(match[2]);
                                      const text = match[4] || contrib;
                                      const timestamp = hours * 3600 + minutes * 60 + seconds;
                                      allMessages.push({
                                        timestamp,
                                        speaker: speaker.name,
                                        role: speaker.role,
                                        text: text,
                                        timeStr: match[1] + ':' + match[2] + (match[3] ? ':' + match[3] : '')
                                      });
                                    } else {
                                      allMessages.push({
                                        timestamp: 999999,
                                        speaker: speaker.name,
                                        role: speaker.role,
                                        text: contrib,
                                        timeStr: ''
                                      });
                                    }
                                  }
                                });
                              }
                            });
                            allMessages.sort((a, b) => a.timestamp - b.timestamp);
                            
                            return allMessages.map((msg, idx) => (
                              <div key={idx} className="flex items-start gap-3 p-3 bg-white dark:bg-gray-700/30 rounded-lg">
                                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ backgroundColor: primaryColor }}>
                                  {msg.speaker[0]?.toUpperCase() || '?'}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{msg.speaker}</span>
                                    {msg.role && <span className="text-xs text-gray-500 dark:text-gray-400">({msg.role})</span>}
                                    {msg.timeStr && <span className="text-xs text-gray-400 dark:text-gray-500">[{msg.timeStr}]</span>}
                                  </div>
                                  <p className="text-sm text-gray-700 dark:text-gray-300">{msg.text}</p>
                                </div>
                              </div>
                            ));
                          })()}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Recording */}
                  {notesData.recording_url && (
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Recording</h4>
                      <audio controls className="w-full">
                        <source src={notesData.recording_url} />
                        Your browser does not support the audio tag.
                      </audio>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

/* Figma: week Sun-Sat, get Sunday as start */
const getWeekStart = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - day);
  return d;
};

const InterviewCalendar = ({
  interviews = [],
  searchId,
  onScheduleInterview,
  defaultExpanded = true,
  className = '',
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [viewMode, setViewMode] = useState('weekly'); // 'weekly' | 'monthly'
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Modal state for interview details
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedInterviews, setSelectedInterviews] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedHour, setSelectedHour] = useState(null);
  const [showAllUpcoming, setShowAllUpcoming] = useState(false);

  const { settings } = useSettings();
  const primaryColor = settings.theme?.colors?.primary || '#3B5BDB';
  const secondaryColor = settings.theme?.colors?.secondary || '#20C997';

  const weekDays = useMemo(() => {
    const start = getWeekStart(currentDate);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [currentDate]);

  const prevWeek = () => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() - 7);
    setCurrentDate(d);
  };

  const nextWeek = () => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() + 7);
    setCurrentDate(d);
  };

  /* Figma: "February, 2026" format with comma */
  const formatMonthYear = (date) => {
    return `${date.toLocaleDateString('en-US', { month: 'long' })}, ${date.getFullYear()}`;
  };

  /* Figma: day name (lighter) above date (bold) - two lines */
  const formatDayHeader = (d) => ({
    day: DAYS_UPPER[d.getDay()],
    date: d.getDate(),
  });

  /* Event variant: blue for Design Review, yellow for 1:1 (per Figma) */
  const getEventStyle = (ev) => {
    const title = (ev.interview_title || ev.candidate_name || '').toLowerCase();
    const isYellow = title.includes('1:1') || title.includes('with heather');
    const accent = isYellow ? '#FACC15' : primaryColor;
    const textColor = isYellow ? '#854D0E' : primaryColor;
    return {
      backgroundColor: `${accent}25`,
      color: textColor,
      borderLeft: `3px solid ${accent}`,
    };
  };

  const getEventsForSlot = (day, hour) => {
    return interviews.filter((ev) => {
      const evDate = ev.scheduled_date ? new Date(ev.scheduled_date) : null;
      const evHour = ev.scheduled_time ? parseInt(ev.scheduled_time.split(':')[0], 10) : null;
      if (!evDate || evHour === null) return false;
      return (
        evDate.getFullYear() === day.getFullYear() &&
        evDate.getMonth() === day.getMonth() &&
        evDate.getDate() === day.getDate() &&
        evHour === hour
      );
    });
  };

  const ChevronIcon = ({ down }) => (
    <svg
      className={`w-4 h-4 transition-transform sm:w-5 sm:h-5 ${down ? '' : 'rotate-180'}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );

  return (
    <div className={`transition-colors duration-250 ${className}`}>
      {expanded ? (
        /* Figma: merged with background, month chip + toggle + schedule at end */
        <div>
          <div className="flex flex-wrap items-center gap-4 sm:gap-6 py-3">
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="flex min-w-0 flex-none items-center justify-start gap-2 text-left hover:opacity-80 transition-opacity cursor-pointer select-none sm:-ml-1"
            >
              <h3 className="text-base font-bold leading-tight text-gray-900 dark:text-white pointer-events-none sm:text-[18px] sm:leading-normal">
                <span className="block sm:inline">Interview</span>
                <span className="block sm:inline sm:ml-1">Calendar</span>
              </h3>
              <ChevronIcon down={false} />
            </button>
            
            {/* Spacer to push controls to the end */}
            <div className="flex-1" />
            
            {/* Month chip - no background, filled arrows */}
            <div className="inline-flex items-center rounded-full px-2 py-1">
              <button type="button" onClick={prevWeek} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors" aria-label="Previous">
                <svg className="w-4 h-4" fill={primaryColor} viewBox="0 0 24 24">
                  <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                </svg>
              </button>
              <span className="px-3 text-sm font-medium text-gray-900 dark:text-white min-w-[120px] text-center">
                {formatMonthYear(weekDays[0] || currentDate)}
              </span>
              <button type="button" onClick={nextWeek} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors" aria-label="Next">
                <svg className="w-4 h-4" fill={primaryColor} viewBox="0 0 24 24">
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                </svg>
              </button>
            </div>
            
            {/* Monthly | Weekly toggle - white unselected, shadow */}
            <div className="inline-flex p-0.5 rounded-lg bg-white dark:bg-gray-800 shadow-md dark:shadow-gray-900/30">
              <button
                type="button"
                onClick={() => setViewMode('monthly')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'monthly' ? 'text-white' : 'text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800'
                }`}
                style={viewMode === 'monthly' ? { background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` } : {}}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setViewMode('weekly')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'weekly' ? 'text-white' : 'text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800'
                }`}
                style={viewMode === 'weekly' ? { background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` } : {}}
              >
                Weekly
              </button>
            </div>
            
            {/* Schedule Interview button - primary color only */}
            <button
              type="button"
              onClick={() => onScheduleInterview?.(searchId)}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity"
              style={{ backgroundColor: primaryColor }}
            >
              Schedule Interview
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="flex min-w-0 flex-none items-center justify-start gap-2 text-left hover:opacity-80 transition-opacity cursor-pointer select-none"
          >
            <h3 className="text-base font-bold leading-tight text-gray-900 dark:text-white pointer-events-none sm:text-[18px] sm:leading-normal">
              <span className="block sm:inline">Interview</span>
              <span className="block sm:inline sm:ml-1">Calendar</span>
            </h3>
            <ChevronIcon down={true} />
          </button>
          
          {/* Quick action buttons when collapsed */}
          <div className="flex flex-shrink-0 items-center gap-3">
            {/* Schedule Interview button - same as expanded view */}
            <button
              type="button"
              onClick={() => onScheduleInterview?.(searchId)}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity"
              style={{ backgroundColor: primaryColor }}
            >
              Schedule Interview
            </button>
          </div>
        </div>
      )}

      {expanded && (
        <div className="pt-2 pb-4">

          {viewMode === 'weekly' && (
            <div className="overflow-x-auto">
              <div className="min-w-[700px] rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                {/* Header row: empty corner, day columns, timezone on right */}
                <div className="grid grid-cols-9">
                  <div className="col-span-1 bg-gray-50 dark:bg-gray-800/50 p-2 border-r border-b border-gray-200 dark:border-gray-600" />
                  {weekDays.map((d) => {
                    const h = formatDayHeader(d);
                    return (
                      <div
                        key={d.toISOString()}
                        className={`p-2 text-center border-r border-b border-gray-200 dark:border-gray-600 ${
                          d.toDateString() === new Date().toDateString()
                            ? 'bg-blue-50 dark:bg-blue-900/20'
                            : 'bg-gray-50 dark:bg-gray-800/50'
                        }`}
                      >
                        <div className="text-[10px] font-medium text-gray-500 dark:text-gray-400 tracking-wide">{h.day}</div>
                        <div className="text-sm font-bold text-gray-900 dark:text-white">{h.date}</div>
                      </div>
                    );
                  })}
                  <div className="p-2 text-right text-[10px] text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50">
                    EST GMT-5
                  </div>
                </div>
                {/* Body: time on left, 7 day columns, time on right */}
                {HOURS.map((hour) => (
                  <div key={hour} className="grid grid-cols-9">
                    <div className="p-1.5 text-xs text-gray-500 dark:text-gray-400 border-r border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50">
                      {hour === 12 ? '12 PM' : hour > 12 ? `${hour - 12} PM` : `${hour} AM`}
                    </div>
                    {weekDays.map((day) => {
                      const slotEvents = getEventsForSlot(day, hour);
                      return (
                        <div
                          key={`${day.toISOString()}-${hour}`}
                          className="p-1 border-r border-b border-gray-200 dark:border-gray-600 min-h-[48px] bg-white dark:bg-gray-800/30 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                          onClick={() => {
                            setSelectedDate(day);
                            setSelectedHour(hour);
                            setSelectedInterviews(slotEvents);
                            setShowAllUpcoming(false);
                            setModalOpen(true);
                          }}
                        >
                          {slotEvents.slice(0, 2).map((ev) => (
                            <div
                              key={ev.id}
                              className="text-xs px-2 py-1.5 rounded truncate mb-1 last:mb-0"
                              style={getEventStyle(ev)}
                              title={ev.interview_title || ev.candidate_name || 'Interview'}
                            >
                              {ev.interview_title || ev.candidate_name || 'Interview'}
                            </div>
                          ))}
                          {slotEvents.length > 2 && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">+{slotEvents.length - 2}</span>
                          )}
                        </div>
                      );
                    })}
                    <div className="p-1.5 text-xs text-gray-400 dark:text-gray-500 border-b border-gray-200 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-800/50 text-right">
                      {hour === 12 ? '12 PM' : hour > 12 ? `${hour - 12} PM` : `${hour} AM`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {viewMode === 'monthly' && (
            <div className="grid grid-cols-7 gap-1 text-sm">
              {DAYS_UPPER.map((d) => (
                <div key={d} className="font-medium text-gray-500 dark:text-gray-400 py-2 text-center text-xs tracking-wide">
                  {d}
                </div>
              ))}
              {(() => {
                const start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
                const startDay = start.getDay();
                const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
                const cells = [];
                for (let i = 0; i < startDay; i++) {
                  cells.push(<div key={`empty-${i}`} className="p-2 min-h-[60px]" />);
                }
                for (let d = 1; d <= daysInMonth; d++) {
                  const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), d);
                  const dayEvents = interviews.filter((ev) => {
                    const ed = ev.scheduled_date ? new Date(ev.scheduled_date) : null;
                    return ed && ed.getFullYear() === date.getFullYear() && ed.getMonth() === date.getMonth() && ed.getDate() === d;
                  });
                  const isToday = date.toDateString() === new Date().toDateString();
                  cells.push(
                    <div
                      key={d}
                      className={`p-2 min-h-[60px] border rounded cursor-pointer hover:shadow-md transition-all ${
                        isToday
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                          : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                      onClick={() => {
                        setSelectedDate(date);
                        setSelectedHour(null);
                        setSelectedInterviews(dayEvents);
                        setShowAllUpcoming(false);
                        setModalOpen(true);
                      }}
                    >
                      <span className="font-medium text-gray-700 dark:text-gray-300">{d}</span>
                      {dayEvents.length > 0 && (
                        <div className="mt-1 space-y-0.5">
                          {dayEvents.slice(0, 2).map((ev) => (
                            <div
                              key={ev.id}
                              className="text-xs truncate px-1 py-0.5 rounded"
                              style={{ backgroundColor: `${primaryColor}20`, color: primaryColor }}
                            >
                              {ev.interview_title || ev.candidate_name}
                            </div>
                          ))}
                          {dayEvents.length > 2 && (
                            <span className="text-xs text-gray-500">+{dayEvents.length - 2}</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                }
                return cells;
              })()}
            </div>
          )}
        </div>
      )}

      {/* Interview Details Modal */}
      <InterviewDetailsModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setShowAllUpcoming(false);
        }}
        interviews={selectedInterviews}
        selectedDate={selectedDate}
        selectedHour={selectedHour}
        primaryColor={primaryColor}
        showAllUpcoming={showAllUpcoming}
      />
    </div>
  );
};

export default InterviewCalendar;
